import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Security } from '@mui/icons-material';
import QRCode from 'qrcode';
import { useWebSocket } from './WebSocketContext';
import {
  beginMfaSetup,
  fetchMfaStatus,
  verifyMfaSetup
} from '../Utils/adminSettingsApi';

const MfaSetupDialog = () => {
  const { isUserLoggedIn, mfaSetupRequired, setMfaSetupRequired, logout } = useWebSocket();
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaProvisioningUri, setMfaProvisioningUri] = useState('');
  const [mfaQrData, setMfaQrData] = useState('');
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const open = isUserLoggedIn && mfaSetupRequired;

  const applyMfaStatus = (data) => {
    if (data?.success) {
        const enabled = !!data.enabled;
        const pending = !!data.pending;
        setMfaSetupRequired(!!data.required && !enabled);
        setMfaPending(pending);
        if (!pending || enabled) {
          setMfaSecret('');
          setMfaProvisioningUri('');
          setMfaQrData('');
          setMfaSetupCode('');
        }
        setMfaError('');
        setMfaMessage('');
      } else if (data?.message) {
        setMfaError(data.message);
      }
  };

  useEffect(() => {
    if (open) {
      fetchMfaStatus()
        .then(applyMfaStatus)
        .catch((error) => setMfaError(error?.message || 'Failed to load MFA status'));
    }
  }, [open]);

  const handleBeginResponse = (data) => {
    setMfaLoading(false);
    if (data?.success) {
        setMfaPending(true);
        setMfaSecret(data.secret || '');
        setMfaProvisioningUri(data.provisioning_uri || '');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaError('');
        setMfaMessage('Enter the code from your authenticator app to continue.');
    } else {
        setMfaError(data?.message || 'Failed to start MFA setup');
    }
  };

  const handleVerifyResponse = (data) => {
    setMfaLoading(false);
    if (data?.success) {
        setMfaPending(false);
        setMfaSetupRequired(!!data.mfa_setup_required);
        setMfaSecret('');
        setMfaProvisioningUri('');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaError('');
        setMfaMessage(data.message || 'MFA enabled');
    } else {
        setMfaError(data?.message || 'Invalid MFA code');
    }
  };

  useEffect(() => {
    let isActive = true;
    if (!mfaProvisioningUri) {
      setMfaQrData('');
      return () => {};
    }

    setMfaQrData('');
    QRCode.toDataURL(mfaProvisioningUri, { margin: 1, width: 200 })
      .then((url) => {
        if (isActive) setMfaQrData(url);
      })
      .catch((err) => {
        console.error('Failed to generate MFA QR code', err);
        if (isActive) setMfaQrData('');
      });

    return () => {
      isActive = false;
    };
  }, [mfaProvisioningUri]);

  useEffect(() => {
    if (!open) {
      setMfaPending(false);
      setMfaSecret('');
      setMfaProvisioningUri('');
      setMfaQrData('');
      setMfaSetupCode('');
      setMfaError('');
      setMfaMessage('');
      setMfaLoading(false);
    }
  }, [open]);

  const handleBeginMfaSetup = () => {
    setMfaLoading(true);
    setMfaError('');
    setMfaMessage('');
    beginMfaSetup()
      .then(handleBeginResponse)
      .catch((error) => {
        setMfaLoading(false);
        setMfaError(error?.message || 'Failed to start MFA setup');
      });
  };

  const handleVerifyMfaSetup = () => {
    if (!mfaSetupCode.trim()) {
      setMfaError('Enter the code from your authenticator app');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    setMfaMessage('');
    verifyMfaSetup(mfaSetupCode.trim())
      .then(handleVerifyResponse)
      .catch((error) => {
        setMfaLoading(false);
        setMfaError(error?.message || 'Invalid MFA code');
      });
  };

  const handleLogout = () => {
    logout();
  };

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
  };

  const canVerify = !!mfaSetupCode.trim() && (mfaSecret || mfaPending);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(20, 20, 40, 0.65)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Security sx={{ color: '#5757b0' }} />
        MFA setup required
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
          To continue, set up multi-factor authentication for your account.
        </Typography>
        {mfaPending && !mfaSecret && (
          <Alert severity="info" sx={{ mb: 2 }}>
            MFA setup is pending. Enter the verification code below to finish, or generate a new secret to restart.
          </Alert>
        )}
        {mfaError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mfaError}
          </Alert>
        )}
        {mfaMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {mfaMessage}
          </Alert>
        )}
        <Button
          variant="outlined"
          onClick={handleBeginMfaSetup}
          disabled={mfaLoading}
          sx={{ textTransform: 'none' }}
        >
          {mfaLoading ? 'Generating...' : (mfaSecret ? 'Regenerate MFA secret' : 'Generate MFA secret')}
        </Button>
        {(mfaSecret || mfaPending) && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            {mfaProvisioningUri && mfaSecret && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {mfaQrData ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="img"
                      src={mfaQrData}
                      alt="MFA QR code"
                      sx={{ width: 180, height: 180, borderRadius: 2, border: '1px solid #e0e0e0' }}
                    />
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Scan with Google Authenticator
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Generating QR code...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {mfaSecret && (
              <TextField
                fullWidth
                label="Authenticator secret"
                value={mfaSecret}
                InputProps={{ readOnly: true }}
                helperText="Enter this secret in your authenticator app if you cannot scan the QR code."
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              label="Verification code"
              value={mfaSetupCode}
              onChange={(e) => setMfaSetupCode(e.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              helperText="Enter the 6-digit code from your authenticator app."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleLogout} color="inherit">
          Log out
        </Button>
        <Button
          onClick={handleVerifyMfaSetup}
          variant="contained"
          disabled={mfaLoading || !canVerify}
          sx={{ backgroundColor: '#5757b0', textTransform: 'none' }}
        >
          {mfaLoading ? 'Verifying...' : 'Verify & enable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MfaSetupDialog;
